import React, { useEffect, useState } from "react";
import { Bot, FileCheck2, FolderPlus, Inbox, Link2, ListTodo, PenLine, Search, ShieldCheck, Undo2 } from "lucide-react";
import { api } from "../../lib/api";
import { TAB_LABELS } from "./InboxDialogs";

const PERIODS = [["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["", "Tout"]];
const sel = "px-2.5 py-1.5 text-xs border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

function Kpi({ icon: Icon, label, value, tone, onClick, id }) {
  return (
    <button onClick={onClick} disabled={!onClick} className={`text-left p-3 rounded-md border bg-white transition-colors ${onClick ? "hover:border-norea-primary/50" : "cursor-default"} ${tone || "border-norea-border"}`} data-testid={`kpi-${id}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-norea-textMuted"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-2xl font-heading font-medium tabular-nums mt-0.5" data-testid={`kpi-${id}-value`}>{value ?? "…"}</div>
    </button>
  );
}

export function InboxFilters({ f, setF, meta }) {
  const [q, setQ] = useState(f.q || "");
  useEffect(() => { const t = setTimeout(() => setF((x) => ({ ...x, q })), 350); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="inbox-filters">
      <div className="inline-flex bg-white border border-norea-border rounded-md p-0.5">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setF({ ...f, period: k })} className={`px-2.5 py-1 text-xs rounded transition-colors ${f.period === k ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`} data-testid={`filter-period-${k || "all"}`}>{l}</button>
        ))}
      </div>
      <select value={f.family} onChange={(e) => setF({ ...f, family: e.target.value })} className={sel} data-testid="filter-family">
        <option value="">Tous les partenaires</option>
        {(meta?.families || []).map((x) => <option key={x.id} value={x.id}>{x.name} ({x.count})</option>)}
      </select>
      <select value={f.tab} onChange={(e) => setF({ ...f, tab: e.target.value })} className={sel} data-testid="filter-tab">
        <option value="">Tous les onglets</option>
        {Object.entries(TAB_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select value={f.mailbox} onChange={(e) => setF({ ...f, mailbox: e.target.value })} className={sel} data-testid="filter-mailbox">
        <option value="">Toutes les boîtes</option>
        {(meta?.mailboxes || []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
      </select>
      <div className="relative flex-1 min-w-[220px]">
        <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-norea-textMuted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Objet, expéditeur, client…" className={`${sel} w-full pl-7`} data-testid="filter-search" />
      </div>
    </div>
  );
}

export default function InboxDashboard({ stats, go }) {
  const s = stats || {};
  const ai = s.ai || {};
  const ap = s.approvals?.by_kind || {};
  const apDetail = [["status", "statuts"], ["email", "réponses"], ["field", "champs"], ["auto_email", "emails auto"], ["auto_whatsapp", "WhatsApp auto"]]
    .filter(([k]) => ap[k]).map(([k, l]) => `${ap[k]} ${l}`).join(" · ");
  return (
    <div className="mb-5 space-y-3" data-testid="inbox-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Kpi id="received" icon={Inbox} label="Emails reçus" value={s.received} onClick={() => go("all")} />
        <Kpi id="approvals" icon={ShieldCheck} label="À approuver" value={s.approvals?.total} tone={s.approvals?.total ? "border-violet-300" : ""} onClick={() => go("approvals")} />
        <Kpi id="unmatched" icon={Link2} label="À rattacher" value={s.by_state?.unmatched} tone={s.by_state?.unmatched ? "border-amber-300" : ""} onClick={() => go("unmatched")} />
        <Kpi id="proposed" icon={Bot} label="Propositions IA" value={s.by_state?.proposed} onClick={() => go("proposed")} />
        <Kpi id="attached" icon={FileCheck2} label="Rattachés" value={s.by_state?.attached} onClick={() => go("attached")} />
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-norea-textMuted px-1" data-testid="inbox-ai-actions">
        <span className="font-medium text-norea-text">Ce que l'IA a fait :</span>
        <span className="inline-flex items-center gap-1"><PenLine className="w-3 h-3" />{ai.resumes ?? 0} résumé(s)</span>
        <span className="inline-flex items-center gap-1"><FileCheck2 className="w-3 h-3" />{ai.ranges ?? 0} pièce(s) rangée(s)</span>
        <span className="inline-flex items-center gap-1"><PenLine className="w-3 h-3" />{ai.remplis ?? 0} champ(s) rempli(s)</span>
        <span className="inline-flex items-center gap-1"><ListTodo className="w-3 h-3" />{ai.taches ?? 0} tâche(s)</span>
        <span className="inline-flex items-center gap-1"><FolderPlus className="w-3 h-3" />{ai.dossiers_crees ?? 0} dossier(s) créé(s)</span>
        <span className="inline-flex items-center gap-1"><Undo2 className="w-3 h-3" />{ai.annules ?? 0} annulé(s)</span>
        <span>· {s.by_state?.ignored ?? 0} ignoré(s)</span>
        {apDetail && <span className="text-violet-700" data-testid="inbox-approvals-detail">· En attente : {apDetail}</span>}
      </div>
    </div>
  );
}
